import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Leaf, Scale } from 'lucide-react';
import { useState } from 'react';

import { prettyChemistry } from './fuelsSustainability';
import { CaseList, FeedbackPanel, OptionButton, ValueBar } from './FuelsSustainabilityShared';
import { SynthesisGreenExplore, SynthesisViewToggle, type SynthesisView } from './SynthesisExploreViews';
import { formatSig, type AnswerCheck } from './gasLaws';
import {
  GREEN_COMPARISONS,
  GREEN_PRINCIPLES,
  checkHigherEconomy,
  checkPrinciple,
  checkRouteEconomy,
  routeEconomies,
  type GreenComparison,
  type GreenPredictionCheck,
  type GreenPrinciple,
  type RouteChoice,
  type RouteKey,
} from './synthesisDesignRoutes';

const ROUTE_KEYS: RouteKey[] = ['A', 'B'];
const CHOICE_LABELS: Record<RouteChoice, string> = {
  A: 'Route A is higher',
  B: 'Route B is higher',
  equal: 'They are equal',
};
const CHOICES: RouteChoice[] = ['A', 'B', 'equal'];

function RouteCard({ entry, routeKey }: { entry: GreenComparison; routeKey: RouteKey }) {
  const route = entry.routes[routeKey];
  return (
    <div className="min-w-0 rounded-2xl border border-hairline bg-surface p-4">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-brand-sky">
        Route {routeKey}
      </p>
      <p className="mt-1 text-sm font-extrabold text-ink">{route.name}</p>
      <p className="mt-2 break-words font-mono text-sm font-bold text-ink">
        {prettyChemistry(route.equation)}
      </p>
      <p className="mt-2 text-xs font-medium leading-5 text-slate2">{route.detail}</p>
    </div>
  );
}

function ComparisonBody({ entry }: { entry: GreenComparison }) {
  const [choice, setChoice] = useState<RouteChoice | null>(null);
  const [prediction, setPrediction] = useState<GreenPredictionCheck | null>(null);
  const [answer, setAnswer] = useState('');
  const [economyCheck, setEconomyCheck] = useState<AnswerCheck | null>(null);
  const [principle, setPrinciple] = useState<GreenPrinciple | null>(null);
  useScienceTutorSection('ComparisonBody', { choice, prediction, answer, principle });
  const economies = routeEconomies(entry);
  const principleCheck = principle ? checkPrinciple(entry, principle) : null;
  const target = entry.routes[entry.calculate];

  if (!economies) {
    return <p className="text-sm font-bold text-ink">This comparison failed its equation checks and is hidden.</p>;
  }

  return (
    <>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {ROUTE_KEYS.map((key) => (
          <RouteCard key={key} entry={entry} routeKey={key} />
        ))}
      </div>

      <fieldset className="mt-5">
        <legend className="text-sm font-extrabold text-ink">1. Which route has the higher atom economy?</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          {CHOICES.map((option) => (
            <OptionButton
              key={option}
              pressed={choice === option}
              onClick={() => {
                setChoice(option);
                setPrediction(null);
              }}
            >
              {CHOICE_LABELS[option]}
            </OptionButton>
          ))}
        </div>
        <button
          type="button"
          disabled={!choice}
          onClick={() => choice && setPrediction(checkHigherEconomy(entry, choice))}
          className="btn-pill-primary mt-3 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Check prediction
        </button>
      </fieldset>

      {prediction ? (
        <div className="mt-4 grid gap-3">
          <FeedbackPanel
            good={prediction.correct}
            title={prediction.correct ? 'Prediction confirmed.' : 'Not this time.'}
            label="Atom economy prediction feedback"
          >
            <p>{prediction.feedback}</p>
          </FeedbackPanel>
          <figure className="min-w-0 rounded-2xl border border-hairline bg-surface p-4">
            <figcaption className="text-sm font-extrabold text-ink">Atom economy from the balanced equations</figcaption>
            <div className="mt-3 grid gap-1.5">
              {ROUTE_KEYS.map((key) => (
                <ValueBar
                  key={key}
                  label={`Route ${key}: ${entry.routes[key].name}`}
                  value={economies[key].result.percent}
                  max={100}
                  valueText={`${formatSig(economies[key].result.percent, 3)}%`}
                  highlight={key === entry.preferred}
                  tone="mint"
                />
              ))}
            </div>
            <p className="mt-2 text-xs font-medium text-slate2">▶ marks the route judged greener overall.</p>
          </figure>
        </div>
      ) : null}

      <form
        className="mt-5 grid gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const cleaned = answer.trim().replace('%', '');
          setEconomyCheck(checkRouteEconomy(entry, cleaned === '' ? Number.NaN : Number(cleaned)));
        }}
      >
        <p className="text-sm font-extrabold text-ink">
          2. Calculate the atom economy of route {entry.calculate} ({target.name}).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1 text-sm font-bold text-slate2">
            Atom economy (%)
            <input
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setEconomyCheck(null);
              }}
              inputMode="decimal"
              aria-label={`Atom economy of route ${entry.calculate} in percent`}
              className="w-36 rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-bold text-ink"
            />
          </label>
          <button type="submit" className="btn-pill-primary">
            Check atom economy
          </button>
        </div>
      </form>
      {economyCheck ? (
        <div className="mt-3">
          <FeedbackPanel good={economyCheck.status === 'correct'} title={economyCheck.title} label="Atom economy feedback">
            <p>{economyCheck.feedback}</p>
          </FeedbackPanel>
        </div>
      ) : null}

      <fieldset className="mt-5">
        <legend className="text-sm font-extrabold text-ink">3. {entry.principleQuestion}</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2 xl:grid-cols-3" role="group" aria-label="Green chemistry principles">
          {GREEN_PRINCIPLES.map((item) => (
            <OptionButton key={item.id} pressed={principle === item.id} onClick={() => setPrinciple(item.id)}>
              <span className="block">
                {item.number}. {item.label}
              </span>
              <span
                className={`mt-0.5 block text-xs font-bold ${principle === item.id ? 'text-white/80' : 'text-slate2'}`}
              >
                {item.meaning}
              </span>
            </OptionButton>
          ))}
        </div>
      </fieldset>
      {principleCheck ? (
        <div className="mt-3">
          <FeedbackPanel
            good={principleCheck.correct}
            title={principleCheck.correct ? 'A reviewed principle for this comparison.' : 'Look again at the trade-off.'}
            label="Principle feedback"
          >
            <p>{principleCheck.feedback}</p>
          </FeedbackPanel>
        </div>
      ) : null}
    </>
  );
}

export function SynthesisDesignGreenMode() {
  const [selected, setSelected] = useState<GreenComparison>(GREEN_COMPARISONS[0]);
  const [view, setView] = useState<SynthesisView>('explore');
  useScienceTutorSection('SynthesisDesignGreenMode', { view, selected: tutorSelection(selected) });

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <CaseList
        heading="Choose a product"
        label="Route comparisons"
        items={GREEN_COMPARISONS.map((entry) => ({
          id: entry.id,
          label: entry.product,
          detail: `${entry.routes.A.name} vs ${entry.routes.B.name}`,
        }))}
        selectedId={selected.id}
        onSelect={(id) => {
          const next = GREEN_COMPARISONS.find((entry) => entry.id === id);
          if (next) setSelected(next);
        }}
      />
      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <div className="mb-4">
          <SynthesisViewToggle view={view} onChange={setView} />
        </div>
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
          <Leaf className="h-4 w-4" aria-hidden="true" /> Green chemistry
        </p>
        <h3 className="mt-1 flex items-center gap-2 text-xl font-extrabold text-ink">
          <Scale className="h-5 w-5 text-slate2" aria-hidden="true" /> Two routes to {selected.product.toLowerCase()}
        </h3>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">
          Atom economy = mass of desired product ÷ total mass of reactants × 100%, using the
          coefficients in the balanced equation.
        </p>
        {view === 'explore' ? (
          <SynthesisGreenExplore key={selected.id} entry={selected} />
        ) : (
          <ComparisonBody key={selected.id} entry={selected} />
        )}
      </section>
    </div>
  );
}
